import { createTheme, responsiveFontSizes, type Palette } from '@mui/material';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';

/*

NOTE  Heston is working on the design so we may change

Mui themes can be augmented with custom properties.

For example, if you want to add "sidebar" to the primary palette

interface Theme {
    palette: {
      primary: {
        sidebar: string;
      };
    };
  }

  interface ThemeOptions {
    palette?: {
      primary?: {
        sidebar?: string;
      };
    };
  }

*/

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SWPalette {
  bg: Palette['primary'] & {
    blue: string;
    grey: string;
    paper: string;
  };
  button: Palette['primary'] & {
    primary: string;
  };
  svgIcons: Partial<Palette['primary']> & {
    primary: {
      fill: string;
    };
    error: {
      fill: string;
    };
  };
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SWPaletteOptions {
  bg?: Partial<Palette['primary']> & {
    blue?: string;
    grey?: string;
    paper?: string;
  };
  button?: Partial<Palette['primary']> & {
    primary?: string;
  };
  svgIcons: Partial<Palette['primary']> & {
    primary: {
      fill: string;
    };
    error: {
      fill: string;
    };
  };
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    large: React.CSSProperties;
    medium: React.CSSProperties;
    bodyBold: React.CSSProperties;
    small: React.CSSProperties;
  }

  // Augment the Palette
  interface Palette extends SWPalette {}
  interface PaletteOptions extends SWPaletteOptions {}

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    large?: React.CSSProperties;
    medium?: React.CSSProperties;
    bodyBold?: React.CSSProperties;
    small?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    large: true;
    medium: true;
    bodyBold: true;
    small: true;
  }
}

const _light = clientThemePalette[CLIENT_THEME].light;
const _dark = clientThemePalette[CLIENT_THEME].dark;

const primary = {
  main: _light.primary,
  dark: _light.primaryDark,
};

export const theme = responsiveFontSizes(
  createTheme({
    palette: {
      primary,
      bg: {
        blue: _light.bgBlue,
        grey: _light.bgGrey,
        paper: _light.white,
      },
      button: {
        primary: _light.primary,
      },
      svgIcons: {
        primary: {
          fill: _light.svgPrimaryFill,
        },
        error: {
          fill: _light.svgErrorFill,
        },
      },
    },
    typography: {
      fontFamily: ['Montserrat', 'Open Sans'].join(','),
      large: {
        fontSize: '16px',
      },
      medium: {
        fontSize: '14px',
        fontFamily: 'Montserrat',
      },
      small: {
        fontSize: '12px',
        fontFamily: 'Open Sans',
      },
      bodyBold: {
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: 'Open Sans',
      },
    },
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            color: 'white',
            backgroundColor: primary.main,
            fontSize: '14px',
            maxWidth: '175px',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          // added styling for disabled state of button => visinle on both themes but greyed out
          root: ({ theme }) => {
            const isDark = theme.palette.mode === 'dark';
            return {
              '&.Mui-disabled': {
                color: isDark ? 'rgba(255, 255, 255, 0.3)' : '#9E9E9E',
                opacity: 1, // override MUI default fade
                borderColor: '#606161',
              },
            };
          },
        },
        defaultProps: {
          sx: (theme) => ({
            fontFamily: 'Montserrat',
          }),
        },
      },

      MuiAlert: {
        styleOverrides: {
          standardSuccess: {
            backgroundColor: '#749A4A',
            color: 'white',
          },
          standardError: {
            backgroundColor: '#A03434',
            color: 'white',
          },
        },
      },
    },
  })
);

export const ThemePalette = {
  light: {
    pageBackground: _light.pageBackground,
    topBar: _light.topBar,
    boxBackground: _light.boxBackground,
    toolBarBackground: _light.toolBarBackground,
    cartesianGrid: _light.cartesianGrid,
    menuOptions: _light.menuOptions,
    tableHeader: _light.tableHeader,
    cardBorderColor: _light.cardBorderColor,
  },
  dark: {
    pageBackground: _dark.pageBackground,
    topBar: _dark.topBar,
    boxBackground: _dark.boxBackground,
    toolBarBackground: _dark.toolBarBackground,
    modalBackground: _dark.modalBackground,
    cartesianGrid: _dark.cartesianGrid,
    menuOptions: _dark.menuOptions,
    tableHeader: _dark.tableHeader,
    cardBorderColor: _dark.cardBorderColor,
  },
  typography: {
    buttonLink: _light.buttonLink,
    primary: _light.primary,
    secondary: _light.textSecondary,
    success: _light.success,
    lightSuccess: _light.lightSuccess,
    danger: _light.danger,
    warning: _light.warning,
    lightDanger: _light.lightDanger,
    info: _light.info,
    wheat: _light.wheat,
    white: _light.white,
    black: _light.black,
    cyan: _light.cyan,
    lightGrey: _light.lightGrey,
    grey: _light.grey,
    cornflowerBlue: _light.cornflowerBlue,
    mediumGray: _light.mediumGray,
    lightDark: _light.lightDark,
    darkGrey: _light.darkGrey,
  },
  border: _light.border,
};
